import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';

const SearchBar = ({ search, onSearchChange, loading }) => {
  const [searchTerm, setSearchTerm] = useState(search);
  const inputRef = useRef(null);
  const onSearchChangeRef = useRef(onSearchChange);

  // Update ref when callback changes
  useEffect(() => {
    onSearchChangeRef.current = onSearchChange;
  }, [onSearchChange]);

  // Update local state when prop changes
  useEffect(() => {
    setSearchTerm(search);
  }, [search]);

  // Always keep focus on the input after any re-render
  useLayoutEffect(() => {
    if (inputRef.current && document.activeElement !== inputRef.current) {
      inputRef.current.focus();
    }
  });

  // Debounced incremental search with 1 second delay
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm.trim().length >= 3 || searchTerm.trim().length === 0) {
        onSearchChangeRef.current(searchTerm);
      }
    }, 1000); // 1 second debounce

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handleSubmit = (e) => {
    e.preventDefault();

    // Trigger immediate search
    if (searchTerm.trim().length >= 3 || searchTerm.trim().length === 0) {
      onSearchChange(searchTerm);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="relative">
      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
        {loading ? (
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600"></div>
        ) : (
          <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
        )}
      </div>
      <input
        ref={inputRef}
        type="text"
        className="block w-full pl-10 pr-20 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
        placeholder="Search contracts (auto-search after 1s, or press Enter)..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        disabled={loading}
      />
      <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
        <button
          type="submit"
          disabled={loading || (searchTerm.trim().length > 0 && searchTerm.trim().length < 3)}
          className="bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white px-3 py-1 rounded text-sm font-medium"
        >
          Search
        </button>
      </div>
    </form>
  );
};

export default SearchBar;